<template>
  <div v-if="noValue && !showEditableField" class="not-set w-100" :class="dynamicClass">
    {{ notSet }}
  </div>
  <editable-field-wrapper
    @enter-pressed="emitEnterPressEvent"
    @escape-pressed="emitEscapePressEvent"
    @set-table-loader="emitTableLoader"
    v-else-if="showEditableField"
    :row="row"
    :name="fieldName"
    :list-value="row[fieldName]"
  />
  <cameras-field v-else-if="isCameras" :content="content" />
  <check-box-field v-else-if="isCheckBox" :content="content" :in-detail-view="inDetailView" />
  <check-box-list-field v-else-if="isCheckBoxList" :content="content" />
  <date-field
    v-else-if="isDate"
    :content="content"
    :type="type"
    :fieldName="fieldName"
    :in-detail-view="inDetailView"
  />
  <expression-field v-else-if="isExpression" :content="content" :in-detail-view="inDetailView" />
  <gps-field v-else-if="isGps" :content="content" />
  <number-field
    v-else-if="isNumber"
    :content="content"
    :in-detail-view="inDetailView"
    :decimal="field.numberOfDecimals"
    :prefix="field.numberPrefix"
    :suffix="field.numberSuffix"
    :thousand-separated="thousandSeparated"
  />
  <password-field v-else-if="isPassword" :content="content" :in-detail-view="inDetailView" />
  <radio-button-list-field v-else-if="isRadio" :content="content" />
  <rating-field v-else-if="isRating" :content="content" :in-detail-view="inDetailView" />
  <signature-field v-else-if="isSignature" :content="content" :type="type" :in-detail-view="inDetailView" />
  <base-range v-else-if="isSlider" :value="content" :read-only="true" />
  <sub-form-field v-else-if="isSubform" :content="content" />
  <text-field v-else-if="isText" :content="content" :type="type" :in-detail-view="inDetailView" />
  <div v-else>{{ content }}</div>
</template>

<script>
import { get, isEmpty } from 'lodash'
import { hasValue } from '../../../utils'
import CamerasField from './CamerasField'
import CheckBoxField from './CheckBoxField'
import CheckBoxListField from './CheckBoxListField'
import DateField from './DateField'
import ExpressionField from './ExpressionField'
import GpsField from './GpsField'
import NumberField from './NumberField'
import PasswordField from './PasswordField'
import RadioButtonListField from './RadioButtonListField'
import RatingField from './RatingField'
import SignatureField from './SignatureField'
import SubFormField from './SubFormField'
import TextField from './TextField'
import { QUERY_BUILDER_COLUMN_FORMATS } from '../lib/list-constants'
import BaseRange from '../../form/controls/BaseRange'
import EditableFieldWrapper from '../editable-field-types/EditableFieldWrapper.vue'
import { FIELD_TYPES } from '../../../consts'

export default {
  name: 'FieldWrapper',
  components: {
    EditableFieldWrapper,
    BaseRange,
    CamerasField,
    CheckBoxField,
    CheckBoxListField,
    DateField,
    ExpressionField,
    GpsField,
    NumberField,
    PasswordField,
    RadioButtonListField,
    RatingField,
    SignatureField,
    SubFormField,
    TextField,
  },
  props: {
    fieldName: {
      type: String,
    },
    row: {
      type: Object,
    },
    inDetailView: {
      type: Boolean,
      default: false,
    },
    showEditableField: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    columns() {
      return this.$get('columns')
    },
    content() {
      return get(this.row, this.fieldName)
    },
    dynamicClass() {
      return this.inDetailView
        ? 'text-start'
        : this.isGridView &&
          ((this.noValue && this.isEmail) ||
            this.isCheckBox ||
            this.isDate ||
            this.isPassword ||
            this.isRating ||
            this.isSignature)
        ? 'text-center'
        : this.isNumber
        ? 'text-end'
        : 'text-start'
    },
    field() {
      return this.columns.find((column) => column.data === this.fieldName)
    },
    format() {
      return get(this.field, 'format')
    },
    isEmail() {
      return this.type === FIELD_TYPES.EMAIL
    },
    isCameras() {
      return this.format === QUERY_BUILDER_COLUMN_FORMATS.document || this.type === FIELD_TYPES.CAMERAS
    },
    isCardView() {
      return this.$get('viewMode') === 'cardView'
    },
    isCheckBox() {
      return [FIELD_TYPES.CHECKBOX, FIELD_TYPES.BOOLEAN].includes(this.type)
    },
    isCheckBoxList() {
      return [FIELD_TYPES.CHECKBOX_LIST, FIELD_TYPES.MULTISELECT, FIELD_TYPES.SQL_CHOSEN].includes(this.type)
    },
    isDate() {
      return this.type.match(/date|time/g) // @regex date vagy time szerepel a type-ban
    },
    isExpression() {
      return this.type === FIELD_TYPES.EXPRESSION_EDITOR
    },
    isGps() {
      return this.type === FIELD_TYPES.GPS
    },
    isGridView() {
      return this.$get('viewMode') === 'gridView'
    },
    isNumber() {
      return this.format === QUERY_BUILDER_COLUMN_FORMATS.number || this.type === FIELD_TYPES.NUMBER
    },
    isPassword() {
      return this.type === FIELD_TYPES.PASSWORD
    },
    isQuery() {
      return !isEmpty(this.$get('queryId'))
    },
    isRadio() {
      return this.type === FIELD_TYPES.RADIO_BUTTON_LIST
    },
    isRating() {
      return this.type === FIELD_TYPES.RATING
    },
    isSignature() {
      return [FIELD_TYPES.W_PAINT, FIELD_TYPES.SIGNATURE].includes(this.type)
    },
    isSlider() {
      return this.type === FIELD_TYPES.SLIDER
    },
    isSubform() {
      return this.type === FIELD_TYPES.SUB_FORM
    },
    isText() {
      return [FIELD_TYPES.EMAIL, FIELD_TYPES.RICH_TEXT, FIELD_TYPES.TEXT, FIELD_TYPES.DROPDOWN, 'string'].includes(
        this.type,
      )
    },
    notSet() {
      return this.$i18n.t('not_set')
    },
    noValue() {
      return (
        ![FIELD_TYPES.SLIDER, FIELD_TYPES.RATING, FIELD_TYPES.NUMBER].includes(this.type) &&
        typeof this.content !== FIELD_TYPES.BOOLEAN &&
        !hasValue(this.content)
      )
    },
    thousandSeparated() {
      if (this.isQuery) {
        const queryFormat = get(this.field, 'format')
        return (
          queryFormat === QUERY_BUILDER_COLUMN_FORMATS['thousandSep'] ||
          queryFormat === QUERY_BUILDER_COLUMN_FORMATS['default']
        )
      }
      return get(this.field, 'thousandSeparated', true)
    },
    type() {
      return get(this.field, 'originalType') || get(this.field, 'type') || ''
    },
  },
  methods: {
    emitEnterPressEvent() {
      this.$emit('enter-pressed')
    },
    emitEscapePressEvent() {
      this.$emit('escape-pressed')
    },
    emitTableLoader(value) {
      this.$emit('set-table-loader', value)
    },
  },
}
</script>

<style lang="scss" scoped>
.not-set {
  color: $color-not-set;
}
</style>
